import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import React, { useEffect, useState } from "react";
import Loading from "../../Component/Loading";
import NormalTable from "../../Component/NormalTable";
import { color } from "../../StyleData/Config";
import { masterAPI } from "../Masters/dataConfig";
import { axiosPrivate } from "../../axios/axios";
import { toUTC } from "../../utils/dateUtil";

const TRANSACTION_TYPE_OPTIONS = [
  { value: "Receipt", label: "Receipt" },
  { value: "VendorReturn", label: "Vendor Return" },
  { value: "Issue", label: "Issue" },
  { value: "IssueReturn", label: "Issue Return" },
  { value: "Movement", label: "Movement" },
  { value: "Adjustment", label: "Adjustment" },
];

const PartTransactionSummary = () => {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [reportLogo, setReportLogo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [summaryDateFrom, setSummaryDateFrom] = useState(
    moment().subtract(3, "months").format("YYYY-MM-DDTHH:mm:ss")
  );
  const [summaryDateTo, setSummaryDateTo] = useState(
    moment().format("YYYY-MM-DDTHH:mm:ss")
  );

  const [stockRoomOptions, setStockRoomOptions] = useState([]);
  const [partOptions, setPartOptions] = useState([]);
  const [stockRoomFilter, setStockRoomFilter] = useState([]);
  const [partFilter, setPartFilter] = useState([]);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState([]);

  useEffect(() => {
    getFilterOptions();
    getSummaryData();
  }, []);

  const getFilterOptions = async () => {
    const stockRoomLink = masterAPI["selectList"].stockRoom;
    await axiosPrivate
      .get(stockRoomLink)
      .then((res) => setStockRoomOptions(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.log(e, "stock room options error");
        setStockRoomOptions([]);
      });

    const partLink = masterAPI["selectList"].partsInventory;
    await axiosPrivate
      .get(partLink)
      .then((res) => setPartOptions(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.log(e, "part options error");
        setPartOptions([]);
      });
  };

  const getSummaryData = async () => {
    setIsLoading(true);
    const link = masterAPI["KPIReport"].getPartTransactionList;
    const payload = {
      createdDateFrom: toUTC(summaryDateFrom).utcISOString,
      createdDateTo: toUTC(summaryDateTo).utcISOString,
      partIds: partFilter.map((p) => p.value),
      stockRoomIds: stockRoomFilter.map((s) => s.value),
      transactionTypes: transactionTypeFilter.map((t) => t.value),
    };
    await axiosPrivate
      .post(link, payload)
      .then((res) => {
        const responseData = res.data.result?? {};
        const rawLogo = responseData.logourl ?? responseData.result?.logourl ?? "";

        const listSource =  responseData.partTransactionSummaryList||[]


        const normalizedLogo =
          !rawLogo || rawLogo.trim() === ""
            ? ""
            : rawLogo.startsWith("data:image") || /^https?:\/\//i.test(rawLogo)
              ? rawLogo
              : `data:image/png;base64,${rawLogo}`;

        setReportLogo(normalizedLogo);
        setSummaryData(listSource);
      })
      .catch((e) => {
        console.log(e, "getPartTransactionList error");
        setReportLogo("");
        setSummaryData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = () => {
    getSummaryData();
  };

  const columns = [
    {
      Header: "Transaction Date",
      accessor: "transactionDate",
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          {cell.value ? moment(cell.value).format("DD/MM/YYYY HH:mm") : "---"}
        </Typography>
      ),
    },
    {
      Header: "Part",
      accessor: "part",
    },
    {
      Header: "Stock Room",
      accessor: "stockRoom",
    },
    {
      Header: "Aisle/Row/Bin",
      accessor: "binRowAisle",
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
    },
    {
      Header: "Quantity",
      accessor: "quantity",
    },
  ];

  const buildTableRows = (rows, cols) =>
    rows.map((row, index) => [
      (index + 1).toString(),
      ...cols.map((col) => {
        if (col.accessor === "transactionDate") {
          return row.transactionDate
            ? moment(row.transactionDate).format("DD/MM/YYYY HH:mm")
            : "---";
        }
        const value = row[col.accessor];
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      }),
    ]);

  const handleDownloadPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm" });


    if (reportLogo) {
      try {
        doc.addImage(reportLogo, "PNG", 14, 8, 22, 22);
      } catch (error) {
        console.log("PDF logo load failed:", error);
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Part Transaction Summary", 145, 20, { align: "center" });

    autoTable(doc, {
      head: [["No", ...columns.map((col) => col.Header)]],
      body: buildTableRows(summaryData, columns),
      startY: 32,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        font: "helvetica",
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fontStyle: "normal",
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      columnStyles: { 0: { cellWidth: 8, halign: "center" } },
    });

    doc.save("Part Transaction Summary.pdf");
  };

  return (
    <Grid container spacing={"1rem"}>
      <Grid item xs={12}>
        <Paper sx={{ padding: "1rem" }}>
          <Grid container alignItems={"center"}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  color: "#2362B4",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={() => {
                    setSummaryOpen(!summaryOpen);
                  }}
                >
                  {summaryOpen ? (
                    <KeyboardArrowUpIcon
                      sx={{ fontSize: "1.7rem", color: "#2362B4" }}
                    />
                  ) : (
                    <KeyboardArrowDown
                      sx={{ fontSize: "1.7rem", color: "#2362B4" }}
                    />
                  )}{" "}
                </IconButton>
                Filter
              </Typography>
            </Grid>
            <Grid container item xs={6} justifyContent={"flex-end"}>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#2362B4", color: "white" }}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Grid>
          </Grid>
          <Collapse in={summaryOpen} timeout={"auto"} unmountOnExit>
            <Grid container spacing={"1.5rem"}>
              <Grid
                container
                item
                xs={12}
                md={3}
                spacing={"1rem"}
                alignItems={"center"}
              >
                <Grid item xs={12} justifyContent={"flex-end"}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                    }}
                  >
                    Date Range
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoItem>
                      <DesktopDatePicker
                        label="Date From"
                        format="DD/MM/YYYY"
                        value={dayjs(summaryDateFrom)}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                        onChange={(newValue) => {
                          setSummaryDateFrom(newValue);
                          if (newValue !== null) {
                            if (`${newValue.$d}` !== "Invalid Date") {
                              const dateFrom = moment(newValue.$d).format(
                                "YYYY-MM-DDTHH:mm:ss"
                              );
                              setSummaryDateFrom(dateFrom);
                            }
                          }
                        }}
                      />
                    </DemoItem>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoItem>
                      <DesktopDatePicker
                        label="Date To"
                        format="DD/MM/YYYY"
                        value={dayjs(summaryDateTo)}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                        onChange={(newValue) => {
                          setSummaryDateTo(newValue);
                          if (newValue !== null) {
                            if (`${newValue.$d}` !== "Invalid Date") {
                              const dateTo = moment(newValue.$d).format(
                                "YYYY-MM-DDTHH:mm:ss"
                              );
                              setSummaryDateTo(dateTo);
                            }
                          }
                        }}
                      />
                    </DemoItem>
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={3}
              >
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                      mb: "1rem",
                    }}
                  >
                    Part
                  </Typography>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={partOptions}
                    value={partFilter}
                    getOptionLabel={(option) => option.text || ""}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChange={(event, value) => setPartFilter(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Part"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={3}
              >
                <Grid item xs={12} justifyContent={"flex-end"} >
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                      mb: "1rem",
                    }}
                  >
                    Stock Room
                  </Typography>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={stockRoomOptions}
                    value={stockRoomFilter}
                    getOptionLabel={(option) => option.text || ""}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChange={(event, value) => setStockRoomFilter(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Stock Room"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={3}
              >
                <Grid item xs={12} >
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                      mb: "1rem",
                    }}
                  >
                    Transaction Type
                  </Typography>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={TRANSACTION_TYPE_OPTIONS}
                    value={transactionTypeFilter}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChange={(event, value) => setTransactionTypeFilter(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Transaction Type"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        {isLoading ? (
          <Loading />
        ) : (
          <Paper sx={{ p: "1rem" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: color.masterPageTitleFontSize,
                  fontWeight: color.masterPageTitleFontWeight,
                  color: "#2362B4",
                }}
              >
                Part Transaction Summary
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={handleDownloadPdf}
              >
                Download PDF
              </Button>
            </Box>
            <Divider
              sx={{ border: "1px solid rgb(0 0 0 / 17%)", mt: "15px", mb: 2 }}
            />
            <NormalTable data={summaryData} columns={columns} />
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default PartTransactionSummary;
